import { useEffect } from "react";
import { useNavigation } from "react-router";
import { useInterval } from "usehooks-ts";
import { create } from "zustand";

import { type PlayerEngineState, playerEngine } from "~/lib/playerEngine";

import { usePlayerActions } from "./usePlayerActions";

type PlayerEngineStore = PlayerEngineState & {
  position: number;
  setState: (state: Partial<PlayerEngineState & { position: number }>) => void;
};
const usePlayerEngineStore = create<PlayerEngineStore>()((set, get) => {
  return {
    playable: null,
    duration: 0,
    volume: 1,
    position: 0,
    playing: false,
    ended: false,
    loading: false,
    currentSequenceId: null,
    usingFallbackSequence: false,
    error: null,
    setState: (state) => {
      const prev = get();
      // only apply updates to values which are different from the current value
      for (const k in state) {
        if (k === "setState") {
          continue;
        }
        //@ts-expect-error linter doesnt understand this is safe
        if (prev[k] !== state[k]) {
          //@ts-expect-error linter doesnt understand this is safe
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          set({ [k]: state[k] });
        }
      }
      set(state);
    },
  };
});
export function PlayerEngineStateProxy() {
  const setState = usePlayerEngineStore((s) => s.setState);
  const playing = usePlayerEngineStore((s) => s.playing);
  const actions = usePlayerActions();
  const nav = useNavigation();

  useEffect(() => {
    // Subscribe directly and sync state on notifications
    const unsubscribe = playerEngine.subscribe(() => {
      const state = playerEngine.getState();
      const position = actions.getPosition();
      setState({
        playing: state.playing,
        ended: state.ended,
        loading: state.loading,
        duration: state.duration,
        volume: state.volume,
        currentSequenceId: state.currentSequenceId,
        usingFallbackSequence: state.usingFallbackSequence,
        playable: state.playable,
        error: state.error,
        position,
      });
    });

    // Initial sync
    const state = playerEngine.getState();
    setState({
      playing: state.playing,
      ended: state.ended,
      loading: state.loading,
      duration: state.duration,
      volume: state.volume,
      currentSequenceId: state.currentSequenceId,
      usingFallbackSequence: state.usingFallbackSequence,
      playable: state.playable,
      error: state.error,
      position: actions.getPosition(),
    });

    return unsubscribe;
  }, [setState, actions]);

  useEffect(() => {
    void nav.location;
    const state = playerEngine.getState();
    setState({
      playing: state.playing,
      ended: state.ended,
      loading: state.loading,
      duration: state.duration,
      volume: state.volume,
      currentSequenceId: state.currentSequenceId,
      usingFallbackSequence: state.usingFallbackSequence,
      playable: state.playable,
      error: state.error,
      position: actions.getPosition(),
    });
  }, [nav.location, setState, actions]);
  useInterval(
    () => {
      setState({ position: actions.getPosition() });
    },
    playing ? 100 : null,
  );

  return null;
}

export function usePlayerEngine() {
  const playerActions = usePlayerActions();
  const store = usePlayerEngineStore();
  return {
    ...(store as Omit<PlayerEngineStore, "setState">),
    ...playerActions,
  };
}
